import {
  useCallback,
  useMemo,
  type Dispatch,
  type SetStateAction,
} from "react";
import { type DateRange, type PropsRange } from "react-day-picker";
import {
  boundsFromMonths,
  boundsFromDisabled,
  intersectDateBounds,
  isValidDate,
  isWithinBounds,
  reduceRangeAfterDayClick,
  startOfLocalDay,
} from "../utils";
import { useMaxRangeWindow } from "./useMaxRangeWindow";

interface UseDateRangeSelectionArgs {
  selected: DateRange | undefined;
  setSelected: Dispatch<SetStateAction<DateRange | undefined>>;
  maxRangeDays?: number;
  disabled?: { before: Date; after: Date } | true;
  startMonth?: Date;
  endMonth?: Date;
}

export function useDateRangeSelection({
  selected,
  setSelected,
  maxRangeDays,
  disabled,
  startMonth,
  endMonth,
}: UseDateRangeSelectionArgs) {
  const allowableBounds = useMemo(() => {
    const hardBounds = boundsFromDisabled(disabled);
    const monthBounds = boundsFromMonths(startMonth, endMonth);
    return intersectDateBounds(hardBounds, monthBounds);
  }, [disabled, endMonth, startMonth]);

  const isDayDisabled = useCallback(
    (d: Date) => !isWithinBounds(startOfLocalDay(d), allowableBounds),
    [allowableBounds],
  );

  const maxRangeWindow = useMaxRangeWindow(selected, maxRangeDays);

  const isOutsideMaxRangeWindow = useCallback(
    (d: Date) => {
      if (maxRangeWindow.min == null || maxRangeWindow.max == null) {
        return false;
      }
      return !isWithinBounds(startOfLocalDay(d), maxRangeWindow);
    },
    [maxRangeWindow],
  );

  const onSelect = useCallback<NonNullable<PropsRange["onSelect"]>>(
    (_nextRange, triggerDate) => {
      if (!isValidDate(triggerDate)) return;
      setSelected((prev) =>
        reduceRangeAfterDayClick(
          prev,
          triggerDate,
          allowableBounds,
          maxRangeDays,
        ),
      );
    },
    [allowableBounds, maxRangeDays, setSelected],
  );

  const canApply = Boolean(selected?.from && selected?.to);

  return {
    selected,
    canApply,
    isDayDisabled,
    isOutsideMaxRangeWindow,
    onSelect,
  };
}
